import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import LangToggle from '../components/wiki/LangToggle'
import TagChips from '../components/wiki/TagChips'
import RelatedEntries from '../components/wiki/RelatedEntries'
import VendorCard from '../components/wiki/VendorCard'
import MarkdownView from '../components/wiki/MarkdownView'
import RoleGate from '../auth/RoleGate'
import { useContext } from 'react'
import { AuthContext } from '../auth/AuthContext'

const toArray = (val) => {
    if (Array.isArray(val)) return val
    if (typeof val === 'string') {
        try { const p = JSON.parse(val); return Array.isArray(p) ? p : [] }
        catch { return [] }
    }
    return []
}

const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '—'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const FILE_IC = {
    pdf: '📕', docx: '📘', doc: '📘', txt: '📄', md: '📄',
    jpg: '🖼️', jpeg: '🖼️', png: '🖼️', csv: '📊', xlsx: '📊',
}

export default function LexisEntry() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { user } = useContext(AuthContext)
    const [entry, setEntry] = useState(null)
    const [clients, setClients] = useState([])
    const [files, setFiles] = useState([])
    const [tags, setTags] = useState([])
    const [lang, setLang] = useState('en')
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState('overview')

    const fetchData = useCallback(async () => {
        setLoading(true)
        try {
            const [entryRes, clientsRes, filesRes] = await Promise.all([
                fetch(`/api/lexis/entries/${id}`).then(r => r.json()),
                fetch(`/api/lexis/entries/${id}/clients`).then(r => r.json()).catch(() => []),
                fetch(`/api/lexis/entries/${id}/files`).then(r => r.json()).catch(() => []),
            ])
            setEntry(entryRes)
            setClients(clientsRes)
            setFiles(filesRes)

            // Fetch tags
            if (entryRes?.tags) {
                setTags(toArray(entryRes.tags))
            }
        } catch (e) {
            console.error('Fetch error:', e)
        }
        setLoading(false)
    }, [id])

    useEffect(() => { fetchData() }, [fetchData])

    if (loading) return <div style={{ padding: '40px', color: '#64748b' }}>Loading...</div>
    if (!entry) return <div style={{ padding: '40px', color: '#ef4444' }}>Entry not found</div>

    const overview = lang === 'vi' ? (entry.overview_vi || entry.overview_en) : entry.overview_en
    const capabilities = entry.capabilities_en || ''
    const capabilityList = capabilities.split('•').filter(s => s.trim())

    // Parse category tags (prefixed with "cat:")
    const categoryTags = tags.filter(t => t.startsWith('cat:')).map(t => t.replace('cat:', ''))
    const regularTags = tags.filter(t => !t.startsWith('cat:'))

    const statusColor = entry.status === 'published' ? '#22c55e' : '#f59e0b'
    const statusLabel = entry.status === 'published' ? 'Published' : 'Draft'

    return (
        <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
            {/* 1. HEADER */}
            <div style={{ marginBottom: '24px' }}>
                <button onClick={() => navigate(-1)} style={{
                    background: 'none', border: 'none', color: '#94a3b8',
                    cursor: 'pointer', fontSize: '14px', marginBottom: '12px',
                }}>← Back</button>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                    <h2 style={{ margin: 0, color: '#f1f5f9', flex: 1 }}>{entry.title}</h2>
                    <span style={{
                        background: `${statusColor}20`, color: statusColor,
                        padding: '4px 12px', borderRadius: '99px', fontSize: '12px', fontWeight: 600,
                    }}>{statusLabel}</span>
                    {entry.category_name && (
                        <span style={{
                            background: '#6366f120', color: '#6366f1',
                            padding: '4px 12px', borderRadius: '99px', fontSize: '12px', fontWeight: 600,
                        }}>{entry.category_name}</span>
                    )}
                    {categoryTags.map((cat, i) => (
                        <span key={i} style={{
                            background: 'rgba(168,85,247,0.15)', color: '#a855f7',
                            padding: '4px 12px', borderRadius: '99px', fontSize: '12px', fontWeight: 600,
                        }}>{cat}</span>
                    ))}
                </div>
                {/* Edit in Wiki button — admin/editor only */}
                {user && ['admin', 'editor'].includes(user.role) && (
                    <button onClick={() => navigate(`/wiki/${entry.id}/edit`)} style={{
                        background: 'rgba(212,175,55,0.15)', color: '#d4af37',
                        border: '1px solid rgba(212,175,55,0.3)', borderRadius: '6px',
                        padding: '6px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: 600,
                    }}>
                        📝 Edit in Wiki
                    </button>
                )}
                <p style={{ color: '#d4af37', margin: '8px 0 0', fontSize: '14px' }}>
                    🏢 {entry.vendor_name} {entry.country_of_origin ? `(${entry.country_of_origin})` : ''}
                </p>
                {entry.last_reviewed_at && (
                    <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '12px' }}>
                        Last reviewed: {new Date(entry.last_reviewed_at).toLocaleDateString()}
                    </p>
                )}
            </div>

            {/* Language toggle */}
            <div style={{ marginBottom: '16px' }}>
                <LangToggle value={lang} onChange={setLang} />
            </div>

            {/* Two-column layout */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '24px', alignItems: 'start' }}>
                {/* LEFT COLUMN (70%) */}
                <div>
                    {/* 2. OVERVIEW */}
                    <div style={{
                        background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
                        border: '1px solid rgba(148,163,184,0.1)', marginBottom: '20px',
                    }}>
                        <h4 style={{ color: '#d4af37', fontSize: '13px', marginTop: 0, marginBottom: '12px' }}>
                            📝 {lang === 'vi' ? 'Tổng quan' : 'Overview'}
                        </h4>
                        <p style={{ color: '#e2e8f0', lineHeight: 1.7, fontSize: '14px', whiteSpace: 'pre-wrap', margin: 0 }}>
                            {overview || 'No overview available.'}
                        </p>
                    </div>

                    {/* 3. CAPABILITIES */}
                    {capabilityList.length > 0 && (
                        <div style={{
                            background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
                            border: '1px solid rgba(148,163,184,0.1)', marginBottom: '20px',
                        }}>
                            <h4 style={{ color: '#d4af37', fontSize: '13px', marginTop: 0, marginBottom: '12px' }}>
                                ⚡ Capabilities
                            </h4>
                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#e2e8f0', fontSize: '14px', lineHeight: 1.8 }}>
                                {capabilityList.map((cap, i) => (
                                    <li key={i} style={{ whiteSpace: 'pre-wrap' }}>{cap.trim()}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* 4. CLIENT MATCH PANEL */}
                    {clients.length > 0 && (
                        <div style={{
                            background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
                            border: '1px solid rgba(148,163,184,0.1)', marginBottom: '20px',
                        }}>
                            <h4 style={{ color: '#d4af37', fontSize: '13px', marginTop: 0, marginBottom: '16px' }}>
                                🎯 Client Match — Vietnamese LEA Agencies
                            </h4>
                            {clients.map((client, i) => (
                                <div key={i} style={{
                                    background: 'rgba(15,23,42,0.6)', borderRadius: '10px', padding: '16px',
                                    border: '1px solid rgba(148,163,184,0.1)', marginBottom: '12px',
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                        <div>
                                            <span style={{
                                                background: 'rgba(99,102,241,0.2)', color: '#6366f1',
                                                padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 700,
                                                marginRight: '8px',
                                            }}>{client.code_name}</span>
                                            <span style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: 600 }}>
                                                {client.full_name_en || client.full_name_vi}
                                            </span>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ color: '#22c55e', fontSize: '13px', fontWeight: 700 }}>
                                                {client.fit_score}/10
                                            </div>
                                            <div style={{
                                                width: '60px', height: '6px', background: 'rgba(148,163,184,0.2)',
                                                borderRadius: '3px', marginTop: '4px',
                                            }}>
                                                <div style={{
                                                    width: `${client.fit_score * 10}%`, height: '100%',
                                                    background: client.fit_score >= 8 ? '#22c55e' : client.fit_score >= 6 ? '#f59e0b' : '#ef4444',
                                                    borderRadius: '3px',
                                                }} />
                                            </div>
                                        </div>
                                    </div>
                                    <p style={{ color: '#94a3b8', fontSize: '13px', margin: '8px 0 12px', lineHeight: 1.5 }}>
                                        {client.fit_reason}
                                    </p>
                                    {client.operation_types && client.operation_types.length > 0 && (
                                        <div>
                                            <span style={{ color: '#64748b', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>
                                                Operations:
                                            </span>
                                            {client.operation_types.map((op, j) => (
                                                <div key={j} style={{
                                                    marginLeft: '12px', marginTop: '6px',
                                                    borderLeft: '2px solid rgba(99,102,241,0.3)', paddingLeft: '10px',
                                                }}>
                                                    <span style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: 600 }}>
                                                        • {op.operation_type}
                                                    </span>
                                                    <p style={{ color: '#94a3b8', fontSize: '12px', margin: '2px 0 0', lineHeight: 1.4 }}>
                                                        {op.use_case}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* 5. OPERATION TYPES TABLE */}
                    {clients.some(c => c.operation_types?.length > 0) && (
                        <div style={{
                            background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
                            border: '1px solid rgba(148,163,184,0.1)', marginBottom: '20px',
                        }}>
                            <h4 style={{ color: '#d4af37', fontSize: '13px', marginTop: 0, marginBottom: '12px' }}>
                                📋 Operations Summary
                            </h4>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.2)' }}>
                                        <th style={{ textAlign: 'left', padding: '8px', color: '#64748b' }}>Operation</th>
                                        <th style={{ textAlign: 'left', padding: '8px', color: '#64748b' }}>Client</th>
                                        <th style={{ textAlign: 'left', padding: '8px', color: '#64748b' }}>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {clients.flatMap(c =>
                                        (c.operation_types || []).map(op => (
                                            <tr key={`${c.code_name}-${op.operation_type}`} style={{ borderBottom: '1px solid rgba(148,163,184,0.1)' }}>
                                                <td style={{ padding: '8px', color: '#e2e8f0' }}>{op.operation_type}</td>
                                                <td style={{ padding: '8px' }}>
                                                    <span style={{
                                                        background: 'rgba(99,102,241,0.2)', color: '#6366f1',
                                                        padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 700,
                                                    }}>{c.code_name}</span>
                                                </td>
                                                <td style={{ padding: '8px', color: '#94a3b8', fontSize: '12px' }}>{op.use_case}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* 6. INTERNAL NOTES (admin/editor only) */}
                    <RoleGate allowedRoles={['admin', 'editor']}>
                        <div style={{
                            background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
                            border: '1px solid rgba(239,68,68,0.2)', marginBottom: '20px',
                        }}>
                            <h4 style={{ color: '#ef4444', fontSize: '13px', marginTop: 0, marginBottom: '12px' }}>
                                🔒 Internal Notes
                            </h4>
                            {entry.internal_notes ? (
                                <MarkdownView content={entry.internal_notes} />
                            ) : (
                                <p style={{ color: '#64748b', fontSize: '13px' }}>No internal notes.</p>
                            )}
                        </div>
                    </RoleGate>
                </div>

                {/* RIGHT COLUMN (30%) — Sticky sidebar */}
                <div style={{ position: 'sticky', top: '20px' }}>
                    {/* 1. SOURCE FILES */}
                    <div style={{
                        background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
                        border: '1px solid rgba(148,163,184,0.1)', marginBottom: '16px',
                    }}>
                        <h4 style={{ color: '#d4af37', fontSize: '13px', marginTop: 0, marginBottom: '12px' }}>
                            📁 Source Files ({files.length})
                        </h4>
                        {files.length === 0 ? (
                            <p style={{ color: '#64748b', fontSize: '13px' }}>No source documents attached</p>
                        ) : (
                            files.map((f, i) => {
                                const ext = (f.file_name || '').split('.').pop()?.toLowerCase() || 'file'
                                const isPdf = ext === 'pdf'
                                return (
                                    <div key={i} style={{
                                        display: 'flex', alignItems: 'center', gap: '8px',
                                        padding: '8px 0', borderBottom: i < files.length - 1 ? '1px solid rgba(148,163,184,0.1)' : 'none',
                                    }}>
                                        <span style={{ fontSize: '18px' }}>{FILE_IC[ext] || '📄'}</span>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                color: '#e2e8f0', fontSize: '12px',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>{f.file_name}</div>
                                            <div style={{ color: '#64748b', fontSize: '11px' }}>{formatFileSize(f.file_size)}</div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                if (isPdf) {
                                                    window.open(f.file_url, '_blank')
                                                } else {
                                                    // Force download for non-PDFs
                                                    const a = document.createElement('a')
                                                    a.href = f.file_url
                                                    a.download = f.file_name
                                                    a.click()
                                                }
                                            }}
                                            style={{
                                                background: 'rgba(99,102,241,0.2)', color: '#6366f1',
                                                border: '1px solid rgba(99,102,241,0.3)', borderRadius: '4px',
                                                padding: '4px 10px', fontSize: '11px', cursor: 'pointer',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >{isPdf ? 'Open' : 'Download'}</button>
                                    </div>
                                )
                            })
                        )}
                    </div>

                    {/* 2. TAGS */}
                    {regularTags.length > 0 && (
                        <div style={{
                            background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
                            border: '1px solid rgba(148,163,184,0.1)', marginBottom: '16px',
                        }}>
                            <h4 style={{ color: '#d4af37', fontSize: '13px', marginTop: 0, marginBottom: '12px' }}>
                                🏷️ Tags
                            </h4>
                            <TagChips tags={regularTags} />
                        </div>
                    )}

                    {/* 3. RELATED ENTRIES */}
                    {entry.related_entry_ids && toArray(entry.related_entry_ids).length > 0 && (
                        <div style={{
                            background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
                            border: '1px solid rgba(148,163,184,0.1)', marginBottom: '16px',
                        }}>
                            <h4 style={{ color: '#d4af37', fontSize: '13px', marginTop: 0, marginBottom: '12px' }}>
                                🔗 Related Entries
                            </h4>
                            {toArray(entry.related_entry_ids).map((relId, i) => (
                                <RelatedEntries key={i} entryId={relId} />
                            ))}
                        </div>
                    )}

                    {/* 4. VENDOR CARD */}
                    {entry.vendor_name && (
                        <div style={{ marginBottom: '16px' }}>
                            <VendorCard
                                vendor={{
                                    name: entry.vendor_name,
                                    country: entry.country_of_origin,
                                    is_represented: entry.is_represented,
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
